// src/App.jsx
import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
  useParams,
  useLocation } from
"react-router-dom";
import { SignIn, SignUp } from "@clerk/clerk-react";

const HomePage = React.lazy(() => import("./pages/Home.jsx"));
const PlatformPage = React.lazy(() => import("./pages/PlatformPage.jsx"));
const SecurityPage = React.lazy(() => import("./pages/SecurityPage.jsx"));
const PrivacyPage = React.lazy(() => import("./pages/PrivacyPage.jsx"));
const MyProjectsPage = React.lazy(() => import("./pages/MyProjectsPage.jsx"));
const InitiativeComingSoonPage = React.lazy(() => import("./pages/InitiativeComingSoon.jsx"));
const CreateProjectPage = React.lazy(() =>
import("./pages/CreateProjectPage.jsx").then((m) => ({ default: m.CreateProjectPage }))
);
const Project = React.lazy(() => import("./pages/Project.jsx"));
const Overview = React.lazy(() => import("./pages/Overview.jsx"));
const ResearchData = React.lazy(() => import("./pages/ResearchData.jsx"));
const StyleGuidePage = React.lazy(() =>
import("./pages/StyleGuidePage.jsx").then((m) => ({ default: m.StyleGuidePage }))
);
const InterviewsPanelPreview = React.lazy(() =>
import("./components/InterviewsPanel.jsx").then((m) => ({ default: m.InterviewsPanel }))
);
const MyAccountPage = React.lazy(() => import("./pages/MyAccountPage.jsx"));
const TeamPage = React.lazy(() => import("./pages/TeamPage.jsx"));
import FeedbackWidget from "./components/FeedbackWidget.jsx";
const FeedbackInbox = React.lazy(() => import("./pages/FeedbackInbox.jsx"));
const AdminDemoArchive = React.lazy(() => import("./pages/AdminDemoArchive.jsx"));

import { OfflineBanner } from "./components/OfflineBanner.jsx";
import { useAppStatusStore } from "./store/useAppStatusStore";

import { useProjectStore } from "./store/useProjectStore";
import { computeAppPhaseForProject } from "./store/selectors";
import { useStorage } from "./storage/StorageProvider.jsx";
import { useAuthState, hasClerkConfig } from "./auth/AuthProvider.jsx";
import { GenerateLoadingOverlay } from "./components/GenerateProjectOverlay.jsx";
import { runProjectGeneration } from "./utils/runProjectGeneration.js";
import { normalizeAIProcessingError } from "./utils/aiProcessingGuards";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import MarketingHeader from "./components/nav/MarketingHeader.jsx";
import AppHeader from "./components/nav/AppHeader.jsx";
import PageSkeleton from "@/components/PageSkeleton.jsx";
import { useProjectPersistence } from "./hooks/useProjectPersistence";
import { buildSignInUrl, normalizeRedirectPath } from "./utils/auth.js";
import {
  DEMO_EMAIL,
  getDemoBrowserId,
  getDemoProjectState,
  hasSeenDemoIntro,
  isSharedDemoUser,
  markDemoIntroSeen,
} from "./utils/demoFlow.js";
import DemoIntroDialog from "./components/demo/DemoIntroDialog.jsx";
import { trackPageView } from "./lib/ga";

const GLOBAL_ERROR_COPY = {
  ai_hourly_limit_reached: {
    title: "AI limit reached for now",
    body:
      "You’ve reached the AI usage limit for this shared demo. Create an account for your own workspace and higher limits.",
    primaryAction: "ok",
    secondaryAction: "create_account",
  },
  ai_daily_limit_reached: {
    title: "Daily AI limit reached",
    body:
      "You’ve used all available AI generations for this shared demo today. Create an account for your own workspace and higher limits.",
    primaryAction: "ok",
    secondaryAction: "create_account",
  },
  ai_cooldown_active: {
    title: "Please wait a moment",
    body: "Please wait a little before generating again.",
    primaryAction: "ok",
    secondaryAction: null,
  },
  ai_regeneration_limit_reached: {
    title: "Regeneration limit reached",
    body:
      "This item has been regenerated too many times in the shared demo. Create an account for more flexibility, or edit it manually.",
    primaryAction: "ok",
    secondaryAction: "create_account",
  },
  project_not_found: {
    title: "Project not found",
    body: "This project could not be found or may no longer be available.",
    primaryAction: "go_to_projects",
    secondaryAction: null,
  },
  project_archived: {
    title: "Project is archived",
    body: "This project is archived and can’t be edited right now.",
    primaryAction: "ok",
    secondaryAction: null,
  },
  project_limit_reached: {
    title: "Project limit reached",
    body:
      "You’ve reached the maximum number of projects available. Remove an existing project or create a private workspace to continue.",
    primaryAction: "ok",
    secondaryAction: "create_private_workspace",
  },
  text_too_long: {
    title: "Input is too long",
    body:
      "This content is too large to process in one go. Try shortening it or splitting it into smaller parts.",
    primaryAction: "ok",
    secondaryAction: "edit_input",
  },
  temporary_unavailable: {
    title: "AI is temporarily unavailable",
    body: "Something went wrong on our side. Please try again in a moment.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  groq_request_too_large: {
    title: "Request is too large",
    body:
      "This generation includes too much content to process at once. Try reducing the input and generating again.",
    primaryAction: "ok",
    secondaryAction: "edit_input",
  },
  groq_bad_request: {
    title: "Couldn’t process request",
    body: "Something in this request couldn’t be processed. Try again or adjust the input.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  generation_timeout: {
    title: "Generation took too long",
    body:
      "The request took longer than expected and couldn’t be completed. Please try again.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  project_create_missing_id: {
    title: "Couldn’t create project",
    body: "Something went wrong while creating the project. Please try again.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  invalid_json_response: {
    title: "Couldn’t process result",
    body: "The result couldn’t be completed properly. Please try again.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  research_limit_exceeded: {
    title: "Research limit reached",
    body:
      "This project has reached its research upload limit. Remove an existing file to add a new one, or create a private workspace for more flexibility.",
    primaryAction: "ok",
    secondaryAction: "create_private_workspace",
  },
  validation_error: {
    title: "Some information is missing",
    body: "Please check the highlighted fields and try again.",
    primaryAction: "ok",
    secondaryAction: null,
  },
  request_too_large: {
    title: "Request is too large",
    body:
      "This generation includes too much content to process at once. Try reducing the input and generating again.",
    primaryAction: "ok",
    secondaryAction: null,
  },
  processing_failed: {
    title: "Couldn’t process request",
    body: "Something in this request couldn’t be processed. Try again or adjust the input.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  groq_request_failed: {
    title: "AI request failed",
    body: "The generation didn’t complete successfully. Please try again.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  empty_generation: {
    title: "Nothing was generated",
    body: "AI didn’t return a usable result this time. Try again.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  truncated_generation: {
    title: "Result was incomplete",
    body: "The generation was cut off before it finished. Try again or reduce the input size.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  json_parse_failed: {
    title: "Couldn’t process result",
    body: "The result couldn’t be completed properly. Please try again.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  groq_retry_failed: {
    title: "Generation failed",
    body:
      "We tried again automatically, but couldn’t complete the request. Please try again later.",
    primaryAction: "try_again",
    secondaryAction: "cancel",
  },
  sample_project_read_only: {
    title: "Demo project is read-only",
    body:
      "This sample project can be explored, but not edited. Create a private workspace to try the full workflow with your own project.",
    primaryAction: "create_private_workspace",
    secondaryAction: "cancel",
  },
  forbidden: {
    title: "You don’t have access",
    body:
      "This action isn’t available in the shared demo. Create an account to use your own private workspace.",
    primaryAction: "ok",
    secondaryAction: "create_account",
  },
};

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    void 0;
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mx-auto mt-8 max-w-3xl rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          Something went wrong while loading this page. Please refresh.
        </div>);

    }
    return this.props.children;
  }
}

/* -----------------------------
 * URL helpers (local, minimal)
 * ----------------------------- */

function parseProjectIdFromKey(projectKey) {
  const key = String(projectKey || "");
  if (!key) return "";
  try {
    const store = useProjectStore.getState();
    if (store?.projects?.byId?.[key]) return key;
  } catch (e) {

    // ignore
  } // If key is UUID (optionally with -slug), extract UUID.
  const uuidMatch = key.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i);
  if (uuidMatch) return uuidMatch[0];
  // Non-UUID ids should be treated as exact ids.
  return key;
}

function buildProjectKeyFromId(projectId) {
  const id = String(projectId || "");
  if (!id) return id;

  try {
    const store = useProjectStore.getState();
    const p = store.projects?.byId?.[id] || null;
    const slug = (p && (p.slug || p.meta?.slug) ? String(p.slug || p.meta?.slug).trim() : "") || "";
    return slug ? `${id}-${slug}` : id;
  } catch (e) {
    // fallback: just id
    return id;
  }
}

function normalizeSectionPath(raw) {
  const path = String(raw || "").replace(/^\/+|\/+$/g, "");
  if (!path) return "overview";

  const direct = {
    research: "research/input",
    insights: "insights/review",
    synthesis: "synthesis/personas",
    decisions: "decisions",
    validate: "validate/experiments",
    tools: "tools/interview-script"
  };
  if (direct[path]) return direct[path];

  const legacy = {
    "research-data": "research/input",
    "research": "research/input",
    "research/notes": "research/input",
    "notes": "research/input",
    "tags": "insights/review",
    "insights/tags": "insights/review",
    "insights": "insights/review",
    "synthesis/themes": "synthesis/personas",
    "story": "synthesis/story",
    "synthesis/story-mode": "synthesis/story",
    "personas": "synthesis/personas",
    "problem-statement": "synthesis/problem-statement",
    "journey": "synthesis/journey",
    "problem-definition": "jtbd",
    "jtbd": "jtbd",
    "opportunity-areas": "decisions",
    "decisions/opportunities": "decisions",
    "how-might-we": "decisions/hmw",
    "interviews": "tools/interview-script",
    "interview-questions": "tools/interview-script",
    "research-questions": "tools/interview-script"
  };
  if (legacy[path]) return legacy[path];

  return path;
}

function useRouteProjectActivation(projectId) {
  const setActiveWorkspace = useProjectStore((s) => s.setActiveWorkspace);
  const setActiveProject = useProjectStore((s) => s.setActiveProject);
  const activeWorkspaceId = useProjectStore((s) => s.activeWorkspaceId);
  const workspacesById = useProjectStore((s) => s.workspaces?.byId || {});
  const activeProjectByWorkspaceId = useProjectStore((s) => s.activeProjectByWorkspaceId);
  const routeProject = useProjectStore(
    useMemo(() => (s) => projectId ? s.projects?.byId?.[projectId] || null : null, [projectId])
  );

  const mappedWorkspaceId = useMemo(() => {
    if (!projectId || !activeProjectByWorkspaceId) return "";
    const match = Object.entries(activeProjectByWorkspaceId).find(([, activeProjectId]) => activeProjectId === projectId);
    return String(match?.[0] || "").trim();
  }, [projectId, activeProjectByWorkspaceId]);

  const routeWorkspaceId = String(
    routeProject?.meta?.workspaceId || routeProject?.workspaceId || mappedWorkspaceId || ""
  ).trim();
  const targetWorkspaceId = routeWorkspaceId || mappedWorkspaceId;
  const activeProjectIdForTargetWorkspace = targetWorkspaceId ?
  activeProjectByWorkspaceId?.[targetWorkspaceId] || null :
  null;
  const activeProjectIdForActiveWorkspace = activeWorkspaceId ?
  activeProjectByWorkspaceId?.[activeWorkspaceId] || null :
  null;

  useEffect(() => {
    if (!projectId || !routeProject) return;
    if (!targetWorkspaceId) {
      const currentProjectId =
      activeProjectIdForActiveWorkspace || activeProjectIdForTargetWorkspace || null;
      if (currentProjectId !== projectId) {
        setActiveProject?.(projectId);
      }
      return;
    }
    if (activeWorkspaceId !== targetWorkspaceId && workspacesById?.[targetWorkspaceId]) {
      setActiveWorkspace?.(targetWorkspaceId);
      return;
    }
    const currentProjectId =
    activeWorkspaceId === targetWorkspaceId ?
    activeProjectIdForTargetWorkspace :
    activeProjectIdForActiveWorkspace;
    if (currentProjectId !== projectId) {
      setActiveProject?.(projectId);
    }
  }, [
  projectId,
  routeProject,
  targetWorkspaceId,
  activeWorkspaceId,
  workspacesById,
  activeProjectIdForTargetWorkspace,
  activeProjectIdForActiveWorkspace,
  setActiveWorkspace,
  setActiveProject]
  );

  const isReady = Boolean(
    projectId &&
    routeProject &&
    activeWorkspaceId &&
    activeProjectIdForActiveWorkspace === projectId
  );

  return { routeProject, routeWorkspaceId: targetWorkspaceId, isReady };
}

// Workspace route wrapper: reads URL params and feeds Project page
function WorkspaceRoute() {
  const navigate = useNavigate();
  const { projectKey, "*": section } = useParams();
  const location = useLocation();

  const safeSection = normalizeSectionPath(section);
  const projectId = parseProjectIdFromKey(projectKey);
  const { routeProject: project } = useRouteProjectActivation(projectId);

  useEffect(() => {
    if (!projectId) return;
    const canonicalKey = buildProjectKeyFromId(projectId);
    if (canonicalKey && canonicalKey !== projectKey) {
      navigate(`/app/projects/${canonicalKey}/${safeSection}${location.search || ""}`, {
        replace: true,
        state: location.state
      });
    }
  }, [projectId, projectKey, safeSection, navigate, project, location.search, location.state]);

  useEffect(() => {
    const normalized = normalizeSectionPath(section);
    if (!section || section !== normalized) {
      navigate(`/app/projects/${projectKey}/${normalized}${location.search || ""}`, {
        replace: true,
        state: location.state
      });
    }
  }, [section, projectKey, navigate, location.search, location.state]);

  if (projectId && !project) {
    return <PageSkeleton />;
  }

  return (
    <Project
      section={safeSection}
      onSectionChange={(next) => navigate(`/app/projects/${projectKey}/${normalizeSectionPath(next)}`)}
      onNavigate={(where) => {
        if (where === "home") navigate("/");else
        if (where === "projects") navigate("/projects");else
        if (where === "create") navigate("/projects/new");else
        if (where === "contact") navigate("/");else
        if (where === "login") alert("Login coming soon 🙂");else
        void 0;
      }}
      projectId={projectId} />);


}

function ReviewShell({ projectName, children }) {
  return (
    <MinimalShell stepLabel="Review insights" projectName={projectName}>
      <div className="mx-auto max-w-6xl px-6 py-6">{children}</div>
    </MinimalShell>);

}

function ReviewContent({ section }) {
  if (section === "insights/review") {
    return <Overview initialMode="insights" forceMode="insights" />;
  }
  if (section === "overview") {
    return <Overview initialMode="overview" forceMode="overview" />;
  }
  if (section === "research/input" || String(section || "").startsWith("research/input/")) {
    return <ResearchData section={section} showBackButton />;
  }
  return <Overview initialMode="insights" forceMode="insights" />;
}

function ProjectEntry() {
  const { projectKey, "*": rawSection } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const section = normalizeSectionPath(rawSection);
  const isRootProjectEntry = !String(rawSection || "").trim();
  const projectId = parseProjectIdFromKey(projectKey);
  const hasHydrated = useProjectStore((s) => s.hasHydrated);
  const projectExists = useProjectStore(
    useMemo(() => (s) => projectId ? Boolean(s?.projects?.byId?.[projectId]) : false, [projectId])
  );
  const isGeneratingProject = useProjectStore((s) => s.isGeneratingProject);
  const generationError = useProjectStore((s) => s.generationError);
  const isInsightsReviewPath = useMemo(() => {
    try {
      return window.location.pathname.includes("/insights/review");
    } catch {
      return false;
    }
  }, []);
  const isProjectPath = useMemo(() => {
    try {
      return location.pathname.startsWith("/app/projects/");
    } catch {
      return false;
    }
  }, [location.pathname]);
  const suppressWizardRedirect = useMemo(() => {
    if (!projectId) return false;
    try {
      return window.sessionStorage.getItem(`insightsFirstLoad:${projectId}`) === "1";
    } catch {
      return false;
    }
  }, [projectId]);
  const justApprovedInsights = useMemo(() => {
    if (!projectId) return false;
    try {
      const raw = window.sessionStorage.getItem(`insightsJustApproved:${projectId}`);
      if (!raw) return false;
      const ts = Number(raw);
      return Number.isFinite(ts) && Date.now() - ts < 2 * 60 * 1000;
    } catch {
      return false;
    }
  }, [projectId, location.pathname]);
  const appPhase = useProjectStore(
    useMemo(() => (s) => computeAppPhaseForProject(s, projectId), [projectId])
  );
  const allowInsightsDuringGeneration = useMemo(() => {
    if (!section.startsWith("insights/")) return false;
    return Boolean(isGeneratingProject || generationError);
  }, [section, isGeneratingProject, generationError]);

  useEffect(() => {
    if (!hasHydrated || !projectExists) return;
    if (justApprovedInsights) {
      try {
        window.sessionStorage.removeItem(`insightsJustApproved:${projectId}`);
      } catch {}
      navigate(`/app/projects/${projectKey}/overview`, { replace: true });
    } else if (appPhase === "wizard" && (isGeneratingProject || generationError)) {
      navigate(`/app/projects/${projectKey}/insights/review`, { replace: true });
    } else if (
    appPhase === "wizard" &&
    !isGeneratingProject &&
    !suppressWizardRedirect &&
    !isInsightsReviewPath &&
    !isProjectPath)
    {
      navigate("/wizard", { replace: true });
    } else if (appPhase === "review" && !justApprovedInsights) {
      navigate(`/app/projects/${projectKey}/insights/review`, { replace: true });
    } else
    navigate(`/app/projects/${projectKey}/overview`, { replace: true });
  }, [
  appPhase,
  navigate,
  projectKey,
  justApprovedInsights,
  hasHydrated,
  projectExists,
  isGeneratingProject,
  generationError,
  suppressWizardRedirect,
  isProjectPath]
  );

  return null;
}

function ProjectGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const storage = useStorage();
  const { user } = useAuthState();
  const { projectKey, "*": rawSection } = useParams();
  const projectId = parseProjectIdFromKey(projectKey);
  const isRootProjectPath = !String(rawSection || "").trim();
  const hasHydrated = useProjectStore((s) => s.hasHydrated);
  const projectExists = useProjectStore(
    useMemo(() => (s) => projectId ? Boolean(s?.projects?.byId?.[projectId]) : false, [projectId])
  );
  const isGeneratingProject = useProjectStore((s) => s.isGeneratingProject);
  const isInsightsReviewPath = useMemo(() => {
    try {
      return window.location.pathname.includes("/insights/review");
    } catch {
      return false;
    }
  }, []);
  const isProjectPath = useMemo(() => {
    try {
      return location.pathname.startsWith("/app/projects/");
    } catch {
      return false;
    }
  }, [location.pathname]);
  const suppressWizardRedirect = useMemo(() => {
    if (!projectId) return false;
    try {
      return window.sessionStorage.getItem(`insightsFirstLoad:${projectId}`) === "1";
    } catch {
      return false;
    }
  }, [projectId]);
  const justApprovedInsights = useMemo(() => {
    if (!projectId) return false;
    try {
      const raw = window.sessionStorage.getItem(`insightsJustApproved:${projectId}`);
      if (!raw) return false;
      const ts = Number(raw);
      return Number.isFinite(ts) && Date.now() - ts < 2 * 60 * 1000;
    } catch {
      return false;
    }
  }, [projectId, location.pathname]);
  const hasPreviouslyApprovedInsights = useProjectStore(
    useMemo(
      () => (s) => {
        if (!projectId) return false;
        const project = s?.projects?.byId?.[projectId] || null;
        const initiativeId =
          s?.activeInitiativeByProjectId?.[projectId] ||
          project?.initiatives?.activeId ||
          project?.initiatives?.allIds?.[0] ||
          null;
        const initiative = initiativeId ? project?.initiatives?.byId?.[initiativeId] || null : null;
        return Boolean(
          initiative?.insightsApprovedSnapshotHash ||
          Number(initiative?.insightsApprovedAt || 0) > 0 ||
          (Array.isArray(initiative?.insightsApprovedSnapshot?.approvedInsights) &&
            initiative.insightsApprovedSnapshot.approvedInsights.length > 0)
        );
      },
      [projectId]
    )
  );
  const section = normalizeSectionPath(rawSection);
  const { routeProject } = useRouteProjectActivation(projectId);
  const mergeProjectSnapshot = useProjectStore((s) => s.mergeProjectSnapshot);
  const [projectLoadRetryTick, setProjectLoadRetryTick] = useState(0);
  const projectLoadRetryCountRef = useRef(0);
  const demoBrowserId = useMemo(() => getDemoBrowserId(), []);
  const currentUser = useProjectStore((s) => s.currentUser);
  const isDemoUser = isSharedDemoUser({
    plan: currentUser?.plan,
    email:
      user?.primaryEmailAddress?.emailAddress ||
      user?.emailAddresses?.[0]?.emailAddress ||
      user?.emailAddress ||
      currentUser?.email ||
      "",
  });
  const projectName = useProjectStore(
    useMemo(
      () => (s) => projectId ? s?.projects?.byId?.[projectId]?.meta?.projectName || "" : "",
      [projectId]
    )
  );
  const appPhase = useProjectStore(
    useMemo(() => (s) => computeAppPhaseForProject(s, projectId), [projectId])
  );
  const allowInsightsDuringGeneration = useMemo(() => {
    if (!section.startsWith("insights/") && !section.startsWith("research/input")) return false;
    return Boolean(isGeneratingProject);
  }, [section, isGeneratingProject]);
  const demoProjectState = useMemo(
    () => (routeProject ? getDemoProjectState(routeProject, demoBrowserId) : null),
    [routeProject, demoBrowserId]
  );

  useEffect(() => {
    if (!projectId || !justApprovedInsights) return;
    if (section !== "overview") return;
    try {
      window.sessionStorage.removeItem(`insightsJustApproved:${projectId}`);
    } catch {}
  }, [projectId, justApprovedInsights, section]);

  useEffect(() => {
    if (!hasHydrated || !projectId || routeProject || !storage?.loadProject) return;
    let cancelled = false;
    let retryTimer = null;

    void (async () => {
      try {
        const snapshot = await storage.loadProject(projectId);
        const data = snapshot?.data || null;
        if (cancelled || !data || typeof data !== "object") return;
        projectLoadRetryCountRef.current = 0;
        try {
          window.sessionStorage.removeItem(`projectLoadHardReloaded:${projectId}`);
        } catch {}
        mergeProjectSnapshot(data);
      } catch (error) {
        if (cancelled) return;
        const nextRetryCount = projectLoadRetryCountRef.current + 1;
        projectLoadRetryCountRef.current = nextRetryCount;

        if (nextRetryCount >= 6) {
          try {
            const reloadKey = `projectLoadHardReloaded:${projectId}`;
            const alreadyReloaded = window.sessionStorage.getItem(reloadKey) === "1";
            if (!alreadyReloaded) {
              window.sessionStorage.setItem(reloadKey, "1");
              window.location.reload();
              return;
            }
          } catch {
            window.location.reload();
            return;
          }
        }

        const retryDelayMs = Math.min(1000 * Math.max(1, nextRetryCount), 7000);
        retryTimer = window.setTimeout(() => {
          if (cancelled) return;
          // Keep retrying in-place while staying on the URL route.
          setProjectLoadRetryTick((v) => v + 1);
        }, retryDelayMs);

        if (nextRetryCount >= 100) {
          // Safety reset to avoid an unbounded counter on very long sessions.
          projectLoadRetryCountRef.current = 20;
          return;
        }
      }
    })();

    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [hasHydrated, projectId, routeProject, storage, mergeProjectSnapshot, projectLoadRetryTick]);

  useEffect(() => {
    if (routeProject) {
      projectLoadRetryCountRef.current = 0;
    }
  }, [routeProject]);

  if (!hasHydrated) {
    return <PageSkeleton />;
  }

  if (projectId && !routeProject) {
    return <PageSkeleton />;
  }

  if (isRootProjectPath && appPhase === "review" && !justApprovedInsights) {
    return <Navigate to={`/app/projects/${projectKey}/insights/review`} replace />;
  }

  if (justApprovedInsights && section !== "overview") {
    return <Navigate to={`/app/projects/${projectKey}/overview`} replace />;
  }

  if (!projectExists) {
    return <PageSkeleton />;
  }

  if (isDemoUser && demoProjectState?.blockedByOther) {
    return <Navigate to="/projects" replace state={{ demoBlocked: true }} />;
  }

  if (
  appPhase === "wizard" &&
  !isGeneratingProject &&
  !suppressWizardRedirect &&
  !isInsightsReviewPath &&
  !isProjectPath)
  {
    return <Navigate to="/wizard" replace />;
  }

  if (appPhase === "wizard" && allowInsightsDuringGeneration) {
    return (
      <ReviewShell projectName={projectName}>
        <ReviewContent section={section} />
      </ReviewShell>);

  }

  if (appPhase === "review") {
    if (section === "overview") {
      if (justApprovedInsights || hasPreviouslyApprovedInsights) {
        return <WorkspaceRoute />;
      }
      return <Navigate to={`/app/projects/${projectKey}/insights/review`} replace />;
    }
    const isAllowedReviewSection =
    section === "insights/review" ||
    section === "research/input" ||
    section.startsWith("research/input/");
    if (!isAllowedReviewSection) {
      if (section === "overview") {
        try {
          if (projectId) window.sessionStorage.setItem(`insightsGateNotice:${projectId}`, "1");
        } catch {}
      }
      return <Navigate to={`/app/projects/${projectKey}/insights/review`} replace />;
    }
    if (hasPreviouslyApprovedInsights) {
      return <WorkspaceRoute />;
    }
    return (
      <ReviewShell projectName={projectName}>
        <ReviewContent section={section} />
      </ReviewShell>);

  }

  return <WorkspaceRoute />;
}

function MinimalShell({ children, stepLabel, projectName }) {
  return <div className="mx-auto max-w-6xl px-6 py-6">{children}</div>;
}

function AppLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const storage = useStorage();
  const { isLoaded, isSignedIn, user, userId, signOut, getToken } = useAuthState();
  const currentUser = useProjectStore((s) => s.currentUser);
  const isGeneratingProject = useProjectStore((s) => s.isGeneratingProject);
  const generationError = useProjectStore((s) => s.generationError);
  const generationPayload = useProjectStore((s) => s.generationPayload);
  const setIsGeneratingProject = useProjectStore((s) => s.setIsGeneratingProject);
  const setGenerationError = useProjectStore((s) => s.setGenerationError);
  const setGenerationPayload = useProjectStore((s) => s.setGenerationPayload);
  const isOverviewGenerating = useProjectStore((s) => s.isOverviewGenerating);
  const setCurrentUser = useProjectStore((s) => s.setCurrentUser);
  const resetProjectState = useProjectStore((s) => s.reset);
  const lastAuthUserIdRef = useRef(null);
  const isDemoUser = isSharedDemoUser({
    plan: currentUser?.plan,
    email:
      currentUser?.email ||
      user?.primaryEmailAddress?.emailAddress ||
      user?.emailAddresses?.[0]?.emailAddress ||
      user?.emailAddress ||
      "",
  });
  const [demoIntroDismissed, setDemoIntroDismissed] = useState(() => hasSeenDemoIntro());
  const showDemoIntro = isDemoUser && !demoIntroDismissed;
  const [globalAiUsageError, setGlobalAiUsageError] = useState(null);
  const isGlobalAiLimitError = (errorLike) => {
    const code = String(errorLike?.code || errorLike?.data?.code || "").trim().toLowerCase();
    const status = Number(errorLike?.status || errorLike?.data?.status || 0);
    const message = String(errorLike?.message || errorLike?.body || errorLike?.data?.error || "").trim().toLowerCase();
    return (
      code.startsWith("ai_") ||
      code === "temporary_unavailable" ||
      status === 429 ||
      message.includes("hourly limit") ||
      message.includes("daily limit") ||
      message.includes("please wait") ||
      message.includes("too many requests")
    );
  };

  // ✅ Global online/offline state
  const setOnline = useAppStatusStore((s) => s.setOnline);
  const setOffline = useAppStatusStore((s) => s.setOffline);

  useEffect(() => {
    const onOnline = () => setOnline();
    const onOffline = () => setOffline();

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // Sync initial state
    if (typeof navigator !== "undefined" && !navigator.onLine) setOffline();else
    setOnline();

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [setOnline, setOffline]);

  useEffect(() => {
    const normalizeDialogPayload = (detail) => {
      const code = String(detail?.code || "").trim();
      const preset = GLOBAL_ERROR_COPY[code] || null;
    if (preset) {
        return {
          title: preset.title,
          body: preset.body,
          code,
          primaryAction: preset.primaryAction || "ok",
          secondaryAction: preset.secondaryAction || null,
        };
      }
      return {
        title: String(detail?.title || "AI generation unavailable").trim(),
        body: String(detail?.body || "Please try again later.").trim(),
        code,
        primaryAction: "ok",
        secondaryAction: null,
      };
    };

    const onAiUsageError = (event) => {
      const detail = event?.detail && typeof event.detail === "object" ? event.detail : null;
      if (!detail) return;
      setGlobalAiUsageError(normalizeDialogPayload(detail));
    };
    window.addEventListener("ai-usage-error", onAiUsageError);
    document.addEventListener("ai-usage-error", onAiUsageError);
    return () => {
      window.removeEventListener("ai-usage-error", onAiUsageError);
      document.removeEventListener("ai-usage-error", onAiUsageError);
    };
  }, []);

  useEffect(() => {
    const presentFromAnyError = (errorLike) => {
      if (!isGlobalAiLimitError(errorLike)) return;
      const code = String(errorLike?.code || errorLike?.data?.code || "").trim();
      const preset = GLOBAL_ERROR_COPY[code] || null;
      if (preset) {
        setGlobalAiUsageError({
          title: preset.title,
          body: preset.body,
          code,
          primaryAction: preset.primaryAction || "ok",
          secondaryAction: preset.secondaryAction || null,
        });
        return;
      }
      const title = String(errorLike?.title || "AI generation unavailable").trim();
      const body = String(errorLike?.body || errorLike?.message || errorLike?.data?.error || "Please try again later.").trim();
      setGlobalAiUsageError({ title, body, code, primaryAction: "ok", secondaryAction: null });
    };
    const onUnhandledRejection = (event) => presentFromAnyError(event?.reason);
    const onWindowError = (event) => presentFromAnyError(event?.error);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onWindowError);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onWindowError);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    const nextAuthUserId = isSignedIn && userId ? String(userId) : "";
    const prevAuthUserId = lastAuthUserIdRef.current;
    if (prevAuthUserId !== null && prevAuthUserId !== nextAuthUserId) {
      resetProjectState?.();
    }
    lastAuthUserIdRef.current = nextAuthUserId;

    if (!isSignedIn || !userId) {
      setCurrentUser(null);
      return;
    }
    const email =
    user?.primaryEmailAddress?.emailAddress ||
    user?.emailAddresses?.[0]?.emailAddress ||
    user?.emailAddress ||
    "";
    const name = user?.fullName || user?.username || "";
    const metaRole = String(user?.publicMetadata?.role || "").trim().toLowerCase();
    const fallbackRole =
      metaRole === "admin" || metaRole === "member" ?
      metaRole :
      "member";
    const planFromMetadata =
      String(user?.publicMetadata?.plan || "free").trim().toLowerCase() === "demo" ? "demo" : "free";
    const isDemoEmailUser = isSharedDemoUser({ plan: planFromMetadata, email });
    const fallbackPlan = isDemoEmailUser ? "demo" : "free";
    let cancelled = false;

    void (async () => {
      let role = fallbackRole;
      let plan = fallbackPlan;
      try {
        const token = typeof getToken === "function" ? await getToken() : null;
        const headers = {
          "Content-Type": "application/json",
          "x-browser-session-id": getDemoBrowserId(),
        };
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetch("/api/auth/context", {
          method: "GET",
          credentials: "include",
          headers,
        });
        if (response.ok) {
          const ctx = await response.json();
          role = ctx?.isAdmin === true ? "admin" : "member";
          plan = ctx?.plan === "demo" ? "demo" : "free";
        }
      } catch {
        // fall back to client metadata-derived role/plan
      }
      if (isDemoEmailUser) {
        plan = "demo";
      }
      if (!cancelled) {
        setCurrentUser({ id: userId, name, email, role, plan });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn, userId, user, setCurrentUser, resetProjectState, getToken]);

  const projectIdFromPath = useMemo(() => {
    const match = location.pathname.match(/\/app\/projects\/([^/]+)/);
    return match ? parseProjectIdFromKey(match[1]) : null;
  }, [location.pathname]);
  useProjectPersistence({
    projectId: projectIdFromPath,
    storage
  });
  const generationHasResearch = Array.isArray(generationPayload?.stagedItemsList) ?
  generationPayload.stagedItemsList.length > 0 :
  false;
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const isTransientGenerationError = (err) => {
    const code = String(err?.code || err?.data?.code || "").trim().toLowerCase();
    const message = String(err?.message || err?.data?.error || "").trim().toLowerCase();
    const status = Number(err?.status || 0);
    return (
      code === "temporary_unavailable" ||
      status === 429 ||
      status === 503 ||
      message.includes("busy right now") ||
      message.includes("temporarily unavailable")
    );
  };
  const runGenerationWithRetry = async ({ storage, projectId, formData, stagedItemsList }) => {
    const hasResearch = Array.isArray(stagedItemsList) && stagedItemsList.length > 0;
    const generationTimeoutMs = hasResearch ? 240000 : 120000;
    const withTimeout = (promise, timeoutMs) =>
      Promise.race([
        promise,
        new Promise((_, reject) => {
          const timeoutError = new Error("Generation timed out.");
          timeoutError.code = "generation_timeout";
          timeoutError.status = 504;
          setTimeout(() => reject(timeoutError), timeoutMs);
        }),
      ]);
    const maxAttempts = 3;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await withTimeout(
          runProjectGeneration({ storage, projectId, formData, stagedItemsList }),
          generationTimeoutMs
        );
      } catch (err) {
        if (attempt < maxAttempts && isTransientGenerationError(err)) {
          await sleep(attempt * 1500);
          continue;
        }
        throw err;
      }
    }
    return null;
  };
  const handleDialogTryAgain = () => {
    const payload = generationPayload && typeof generationPayload === "object" ? generationPayload : null;
    setGlobalAiUsageError(null);
    if (!payload?.projectId) return;
    setGenerationError(null);
    setIsGeneratingProject(true);
    void runGenerationWithRetry({
      storage,
      projectId: payload.projectId,
      formData: payload.formData,
      stagedItemsList: payload.stagedItemsList,
    })
      .then(() => {
        setIsGeneratingProject(false);
      })
      .catch((err) => {
        setGenerationError(normalizeAIProcessingError(err));
        setIsGeneratingProject(true);
      });
  };
  const handleDialogCreatePrivateWorkspace = async () => {
    try {
      await signOut?.();
    } catch {}
    setGlobalAiUsageError(null);
    navigate("/sign-up?redirect=%2Fprojects", { replace: true });
  };
  const handleDialogEditInput = () => {
    setGlobalAiUsageError(null);
    navigate("/projects/new", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F6F8FA]">
      <GenerateLoadingOverlay
        open={isGeneratingProject || Boolean(generationError)}
        error={generationError}
        stepAdvanceMs={generationHasResearch ? 3600 : 1500}
        fakeProgressTimeConstant={generationHasResearch ? 2.2 : 1.2}
        onRetry={
        generationPayload ?
        () => {
          setGenerationError(null);
          setIsGeneratingProject(true);
          void runGenerationWithRetry({
              storage,
              projectId: generationPayload.projectId,
              formData: generationPayload.formData,
              stagedItemsList: generationPayload.stagedItemsList
            }).
          then(() => {
            setIsGeneratingProject(false);
          }).
          catch((err) => {
            setGenerationError(normalizeAIProcessingError(err));
            setIsGeneratingProject(true);
          });
        } :
        null
        }
        onNewProject={() => {
          setGenerationError(null);
          setIsGeneratingProject(false);
          setGenerationPayload(null);
          navigate("/projects/new", { replace: true });
        }} />

      <GenerateLoadingOverlay
        open={isOverviewGenerating}
        variant="overview"
        title="Generating your overview"
        subtitle="Synthesizing approved insights into a clear decision-ready summary…"
        steps={[
        "Collecting approved insights",
        "Synthesizing patterns",
        "Drafting overview sections",
        "Finalizing evidence links"]
        } />

      <AppHeader />

      <OfflineBanner />
      <Dialog
        open={Boolean(globalAiUsageError)}
        onOpenChange={(open) => {
          if (!open) setGlobalAiUsageError(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{globalAiUsageError?.title || "AI generation unavailable"}</DialogTitle>
            <DialogDescription>{globalAiUsageError?.body || "Please try again later."}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            {globalAiUsageError?.secondaryAction === "cancel" ? (
              <Button type="button" variant="secondary" onClick={() => setGlobalAiUsageError(null)}>
                Cancel
              </Button>
            ) : null}
            {globalAiUsageError?.secondaryAction === "edit_input" ? (
              <Button type="button" variant="secondary" onClick={handleDialogEditInput}>
                Edit input
              </Button>
            ) : null}
            {globalAiUsageError?.secondaryAction === "create_account" ||
            globalAiUsageError?.secondaryAction === "create_private_workspace" ? (
              <Button
                type="button"
                variant="secondary"
                onClick={handleDialogCreatePrivateWorkspace}
              >
                Create a private workspace
              </Button>
            ) : null}
            {globalAiUsageError?.primaryAction === "create_account" ||
            globalAiUsageError?.primaryAction === "create_private_workspace" ? (
              <Button
                type="button"
                onClick={handleDialogCreatePrivateWorkspace}
              >
                Create a private workspace
              </Button>
            ) : globalAiUsageError?.primaryAction === "go_to_projects" ? (
              <Button
                type="button"
                onClick={() => {
                  setGlobalAiUsageError(null);
                  navigate("/projects", { replace: true });
                }}
              >
                Go to projects
              </Button>
            ) : globalAiUsageError?.primaryAction === "try_again" ? (
              <Button type="button" onClick={handleDialogTryAgain}>
                Try again
              </Button>
            ) : (
              <Button type="button" onClick={() => setGlobalAiUsageError(null)}>
                OK
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <main className="flex-1">
        <Outlet />
      </main>
      <DemoIntroDialog
        open={Boolean(showDemoIntro)}
        onContinue={() => {
          markDemoIntroSeen();
          setDemoIntroDismissed(true);
        }}
        createAccountHref="/sign-up?redirect=%2Fprojects"
        onCreateAccount={async () => {
          try {
            await signOut?.();
          } catch (error) {
            void error;
          }
          markDemoIntroSeen();
          setDemoIntroDismissed(true);
          navigate("/sign-up?redirect=%2Fprojects", { replace: true });
        }}
      />
      <FeedbackWidget hideLauncher />
    </div>);

}

function MarketingLayout() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <MarketingHeader />
      <Outlet />
    </div>);

}

function MarketingPlaceholder({ title, description }) {
  return (
    <section className="mx-auto max-w-5xl px-6 py-16 sm:px-8">
      <div className="rounded-2xl border border-slate-200 bg-white p-10 shadow-sm">
        <h1 className="text-3xl font-semibold text-slate-900">{title}</h1>
        <p className="mt-3 text-slate-600">
          {description || "This page is coming soon."}
        </p>
      </div>
    </section>);

}

function AppPlaceholder({ title, description }) {
  return (
    <section className="mx-auto max-w-4xl px-6 py-12 sm:px-8">
      <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
        <p className="mt-2 text-slate-600">
          {description || "This page is under construction."}
        </p>
      </div>
    </section>);

}

/* -----------------------------
 * App
 * ----------------------------- */

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn } = useAuthState();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search || ""}`);
  }, [location.pathname, location.search]);

  const openSignIn = (redirectTo, options) => {
    navigate(buildSignInUrl(redirectTo, options));
  };

  const onOpenProject = (projectId) => {
    const id = String(projectId || "").trim();
    if (!id) return;
    // Bypass ProjectEntry redirect heuristics; open the clicked project directly.
    navigate(`/app/projects/${encodeURIComponent(id)}/overview`);
  };

  const onCreateComplete = (projectId) => {
    const projectKey = buildProjectKeyFromId(projectId);
    navigate(`/app/projects/${projectKey}/insights/review`);
  };

  const RequireAuth = ({ children }) => {
    const { isSignedIn, isLoaded } = useAuthState();
    if (!isLoaded) return null;
    if (!isSignedIn) return <Navigate to="/sign-in" replace />;
    return children;
  };

  const RequireSignedIn = ({ children, redirectTo }) => {
    const { isSignedIn, isLoaded } = useAuthState();
    if (!isLoaded) return null;
    if (!isSignedIn) return <Navigate to={buildSignInUrl(redirectTo || location.pathname || "/projects/new")} replace />;
    return children;
  };

  const RequireAdmin = ({ children }) => {
    const { isSignedIn, isLoaded } = useAuthState();
    const currentUser = useProjectStore((s) => s.currentUser);
    if (!isLoaded) return null;
    if (!isSignedIn) return <Navigate to="/sign-in" replace />;
    if (!currentUser) return null;
    if (currentUser?.role !== "admin") return <Navigate to="/projects" replace />;
    return children;
  };

  const AuthRoute = ({ mode }) => {
    const redirectParam = new URLSearchParams(location.search).get("redirect");
    const afterAuthUrl = normalizeRedirectPath(redirectParam, "/projects");
    if (!hasClerkConfig) {
      return (
        <div className="max-w-3xl mx-auto px-8 py-16">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Sign-in unavailable</h1>
          <p className="text-slate-600">
            Clerk is not configured. Set <code>VITE_CLERK_PUBLISHABLE_KEY</code> to enable sign-in.
          </p>
        </div>);

    }
    if (mode === "sign-up") {
      return (
        <div className="mx-auto flex w-full max-w-md flex-col items-center gap-3 px-4 py-10">
          <SignUp
            routing="path"
            path="/sign-up"
            signInUrl="/sign-in"
            afterSignUpUrl={afterAuthUrl} />

          <p className="max-w-xs text-center text-xs text-slate-500">
            By creating an account, you agree to our{" "}
            <a href="/privacy" className="text-slate-600 hover:text-slate-800 underline">
              Privacy Policy
            </a>
            .
          </p>
        </div>);

    }
    const demoSignIn = new URLSearchParams(location.search).get("demo") === "1";
    return (
      <div className="mx-auto flex w-full max-w-md flex-col items-center gap-3 px-4 py-10">
        <SignIn
          routing="path"
          path="/sign-in"
          signUpUrl="/sign-up"
          afterSignInUrl={afterAuthUrl}
          initialValues={demoSignIn ? { emailAddress: DEMO_EMAIL } : undefined}
        />
      </div>);


  };

  const isGeneratingProject = useProjectStore((s) => s.isGeneratingProject);
  const generationError = useProjectStore((s) => s.generationError);
  const generationPayload = useProjectStore((s) => s.generationPayload);

  useEffect(() => {
    const onWizardPath = location.pathname.startsWith("/wizard");
    if (!onWizardPath) return;
    const targetId = generationPayload?.projectId || null;
    if (!targetId) return;
    let allowWizardRetry = false;
    try {
      const raw = window.sessionStorage.getItem("allowWizardRetryUntil");
      allowWizardRetry = raw ? Date.now() < Number(raw) : false;
    } catch {}
    if (allowWizardRetry) return;
    if (isGeneratingProject || generationError) {
      console.log("[App] create_flow_redirect_to_insights", {
        pathname: location.pathname,
        targetId,
        isGeneratingProject: Boolean(isGeneratingProject),
        hasGenerationError: Boolean(generationError),
      });
      navigate(`/app/projects/${targetId}/insights/review`, { replace: true });
    }
  }, [location.pathname, generationPayload, isGeneratingProject, generationError, navigate]);

  return (
    <Suspense fallback={null}>
      <Routes>
        <Route element={<MarketingLayout />}>
          <Route
            path="/"
            element={
            <HomePage
              onNavigate={(where) => {
                if (where === "projects") {
                  if (!isSignedIn) return openSignIn("/projects");
                  navigate("/projects");
                } else if (where === "create") {
                  if (!isSignedIn) return openSignIn("/projects/new");
                  navigate("/projects/new");
                } else if (where === "view-demo") {
                  if (!isSignedIn) {
                    openSignIn("/projects", { demo: true });
                    return;
                  }
                  navigate("/projects");
                } else
                if (where === "security") navigate("/security");else
                if (where === "privacy") navigate("/privacy");else
                if (where === "contact") navigate("/contact");else
                if (where === "login") openSignIn("/projects");else
                navigate("/");
              }}
              currentPage="home" />

            } />

          <Route
            path="/platform"
            element={
            <PlatformPage
              onNavigate={(where) => {
                if (where === "projects") {
                  if (!isSignedIn) return openSignIn("/projects");
                  navigate("/projects");
                } else if (where === "create") {
                  if (!isSignedIn) return openSignIn("/projects/new");
                  navigate("/projects/new");
                } else
                if (where === "security") navigate("/security");else
                if (where === "privacy") navigate("/privacy");else
                if (where === "contact") navigate("/contact");else
                if (where === "login") openSignIn("/projects");else
                navigate("/platform");
              }} />

            } />

          <Route
            path="/security"
            element={
            <SecurityPage
              onNavigate={(where) => {
                if (where === "projects") {
                  if (!isSignedIn) return openSignIn("/projects");
                  navigate("/projects");
                } else if (where === "create") {
                  if (!isSignedIn) return openSignIn("/projects/new");
                  navigate("/projects/new");
                } else
                if (where === "contact") navigate("/contact");else
                if (where === "privacy") navigate("/privacy");else
                if (where === "login") openSignIn("/projects");else
                navigate("/security");
              }} />

            } />

          <Route path="/privacy" element={<PrivacyPage />} />
          <Route
            path="/contact"
            element={
            <MarketingPlaceholder
              title="Contact"
              description="Send us a note and we will get back to you." />

            } />

          <Route
            path="/docs"
            element={
            <MarketingPlaceholder
              title="Docs"
              description="Documentation is on the way." />

            } />

          <Route path="/sign-in/*" element={<AuthRoute mode="sign-in" />} />
          <Route path="/sign-up/*" element={<AuthRoute mode="sign-up" />} />
        </Route>

        <Route element={<AppLayout />}>
          <Route
            path="/app"
            element={
            <RequireAuth>
                <Navigate to="/projects" replace />
              </RequireAuth>
            } />

          <Route
            path="/projects"
            element={
            <RequireAuth>
                <RouteErrorBoundary>
                  <MyProjectsPage
                  onNavigate={(where, payload) => {
                    const buildCreateUrl = () => {
                      if (!payload || typeof payload !== "object") return "/projects/new";
                      const params = new URLSearchParams();
                      if (payload.parentProjectId) params.set("parentProjectId", payload.parentProjectId);
                      if (payload.mode) params.set("mode", payload.mode);
                      const qs = params.toString();
                      return qs ? `/projects/new?${qs}` : "/projects/new";
                    };
                    if (where === "home") navigate("/");else
                    if (where === "projects") navigate("/projects");else
                    if (where === "create") navigate(buildCreateUrl(), { state: payload || null });else
                    if (where === "contact") navigate("/contact");else
                    if (where === "login") openSignIn("/projects");else
                    void 0;
                  }}
                  onOpenProject={onOpenProject} />

                </RouteErrorBoundary>
              </RequireAuth>
            } />

          <Route
            path="/projects/new"
            element={
            <RequireSignedIn redirectTo="/projects/new">
                <CreateProjectPage
                onComplete={onCreateComplete}
                onNavigate={(where) => {
                  if (where === "home") navigate("/");else
                  if (where === "projects") navigate("/projects");else
                  if (where === "create") navigate("/projects/new");else
                  if (where === "contact") navigate("/contact");else
                  if (where === "login") openSignIn("/projects/new");else
                  void 0;
                }} />

              </RequireSignedIn>
            } />

          <Route
            path="/projects/initiatives/coming-soon"
            element={
            <RequireAuth>
                <InitiativeComingSoonPage />
              </RequireAuth>
            } />

          <Route
            path="/style-guide"
            element={
            <RequireAuth>
                <StyleGuidePage />
              </RequireAuth>
            } />


          <Route
            path="/app/interviews-panel"
            element={
            <RequireAuth>
                <div className="mx-auto w-full max-w-6xl px-6 py-6">
                  <InterviewsPanelPreview />
                </div>
              </RequireAuth>
            } />

          <Route
            path="/wizard"
            element={
            <RequireSignedIn redirectTo="/wizard">
                <CreateProjectPage
                onComplete={onCreateComplete}
                onNavigate={(where) => {
                  if (where === "home") navigate("/");else
                  if (where === "projects") navigate("/projects");else
                  if (where === "create") navigate("/wizard");else
                  if (where === "contact") navigate("/contact");else
                  if (where === "login") openSignIn("/wizard");else
                  void 0;
                }} />

              </RequireSignedIn>
            } />


          {/* Workspace deep links */}
          <Route
            path="/app/projects/:projectKey"
            element={
            <RequireAuth>
                <ProjectGate />
              </RequireAuth>
            } />

          <Route
            path="/app/projects/:projectKey/*"
            element={
            <RequireAuth>
                <ProjectGate />
              </RequireAuth>
            } />

          <Route
            path="/admin/feedback"
            element={
            <RequireAdmin>
                <FeedbackInbox />
              </RequireAdmin>
            } />

          <Route
            path="/admin/demo-archive"
            element={
            <RequireAdmin>
                <AdminDemoArchive />
              </RequireAdmin>
            } />

          <Route
            path="/feedback"
            element={
            <RequireAuth>
                <AppPlaceholder
                title="Feedback"
                description="Thanks for sharing. Use the feedback widget in the bottom-right to send us a note." />

              </RequireAuth>
            } />

          <Route
            path="/account"
            element={
            <RequireAuth>
                <MyAccountPage />
              </RequireAuth>
            } />

          <Route
            path="/team"
            element={
            <RequireAuth>
                <TeamPage />
              </RequireAuth>
            } />

        </Route>

        {/* fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>);

}
